import type { ReactNode } from 'react';
import track from 'react-tracking';
import { Row, Column } from '@simplywallst/ui-core';

import {
  SubSectionTitle,
  ReportSubSectionWrapper,
  SubSectionTitleWrapper,
} from './styled';
import { slugify } from '@utilities/slugify';
import { useDwellTracking } from '@/pages/CompanyReport/components/DwellTrackingProvider';
import { useReportSection } from '@/pages/CompanyReport/components/ReportSectionProvider';
import type { HeadingElements } from '@simplywallst/ui-core/lib/src/components/Heading/types';
import IntersectionRenderer from '@/pages/CompanyReport/components/IntersectionRenderer';

type RenderProp = (title: ReactNode) => ReactNode;

export interface ReportSubSectionProps {
  body?: ReactNode;
  bodyRender?: RenderProp;
  split?: boolean;
  statements?: ReactNode;
  title?: ReactNode;
  titleElement?: HeadingElements;
  description?: ReactNode;
  trackingProps?: Record<string, string>;
  unOrdered?: boolean;
  headerActions?: ReactNode;
}

const defaultProps = {
  split: false,
};

const ReportSubSection = ({
  body,
  bodyRender,
  split,
  statements,
  title,
  titleElement = 'h3',
  description,
  unOrdered,
  headerActions,
}: ReportSubSectionProps) => {
  const { observe } = useDwellTracking();
  const { section } = useReportSection();

  const titleText = typeof title === 'string' ? title : '';

  return (
    <>
      <ReportSubSectionWrapper
        ref={observe}
        data-cy-id={`report-sub-section-${slugify(titleText)}`}
        data-section={section}
        split={split}
        titleElement={titleElement}
      >
        <SubSectionTitleWrapper>
          {title && (
            <SubSectionTitle
              as={titleElement}
              unOrdered={unOrdered}
              data-cy-id={`report-sub-section-title-${slugify(titleText)}`}
            >
              {title}
            </SubSectionTitle>
          )}
          {headerActions ? headerActions : null}
        </SubSectionTitleWrapper>
        <span data-cy-id={`${slugify(titleText)}-description`}>
          {typeof description === 'string' ? <p>{description}</p> : description}
        </span>
        <IntersectionRenderer>
          <Row>
            {bodyRender && title && (
              <Column col={12} colLg={split ? 6 : 12}>
                {bodyRender(title)}
              </Column>
            )}
            {body && (
              <Column col={12} colLg={split ? 6 : 12}>
                {body && body}
              </Column>
            )}
            {statements && (
              <Column col={12} colLg={split ? 6 : 12}>
                {statements}
              </Column>
            )}
          </Row>
        </IntersectionRenderer>
      </ReportSubSectionWrapper>
    </>
  );
};

ReportSubSection.defaultProps = defaultProps;

export default track(({ title, trackingProps }) => ({
  type: 'track',
  sectionTitle: title,
  ...trackingProps,
}))(ReportSubSection);
